import { Type , type , boolean } from 'io-ts';

import { Optic , set , get1 , dot , pdot , withDefault } from '~/src/base/Optic';
import { Figure } from '~/src/figure';
import * as Fig from '~/src/figure/Piece';
import { mem } from '~/src/base/Memoize';
import { V2 } from '~/src/geometry/vector';
import * as Vec from '~/src/geometry/vector';
import { P2 , P3 } from '~/src/geometry/point';
import * as Pnt from '~/src/geometry/point';
import { Size } from '~/src/hooks/useSize';
import { NonEmpty } from '~/src/base/Array/NonEmpty';

import Tweaks from './Tweaks';
import { IId } from '~/src/figure/Item';
import { _RulerById, defFigureMod, mergeFigMod } from '~/src/figure/FigureMod';
import { _Info, RulerMod } from '~/src/figure/mod/ruler';
import { _LabelLayer, _Layer } from '~/src/figure/mod/ruler-info';
import { Mod , comp } from '~/src/base/Function';


export interface Piece {
	pan : V2;
	ctrd : boolean;
	zoom : number;
	angle : number;
	tweaks : Tweaks;
}

export const ppan : Optic< Piece , V2 > = dot( 'pan' );
export const pzoom : Optic< Piece , number > = dot( 'zoom' );
export const pctrd : Optic< Piece , boolean > = dot( 'ctrd' );
export const pangle : Optic< Piece , number > = dot( 'angle' );
export const ptweaks : Optic< Piece , Tweaks > = dot( 'tweaks' );

export const defPiece : Piece = {
	pan    : [ 0 , 0 ],
	zoom   : 1,
	ctrd   : true,
	angle  : 0,
	tweaks : defFigureMod,
};


export interface Config {
	editable : boolean;
}

export const Config : Type< Config > = type( { editable : boolean } );

export const editable : Optic< Config , boolean > = dot( 'editable' );

export const defConfig : Config = { editable : false };


export interface State {
	size : Size;
	figure : Figure;
	tweaks : Tweaks;
	pieces : Record< string , Piece >;
	active : string;
	config : Config;
}

export default State;

export const size : Optic< State , Size > = dot( 'size' );
export const figure : Optic< State , Figure > = dot( 'figure' );
export const tweaks : Optic< State , Tweaks > = dot( 'tweaks' );
export const active : Optic< State , string > = dot( 'active' );
export const config : Optic< State , Config > = dot( 'config' );
export const pieces : Optic< State , Record< number , Piece > > = dot( 'pieces' );
export const ipiece = ( index : string ) : Optic< State , Piece > =>
	comp( pieces , pdot( index ) , withDefault( defPiece ) );
export const gpiece = ( active : string ) : Optic< State , Piece > =>
	comp( pieces , pdot( active ) , withDefault( defPiece ) );
export const cpiece = ( f : Mod< Piece > ) => ( s : State ) : State => {
	const opiece : Piece = get1( gpiece( s.active ) )( s );
	const npiece : Piece = f( opiece );

	if ( opiece === npiece ) return s;

	return set( gpiece( s.active ) )( npiece )( s );
};
export const ctweaks : Optic< State , Tweaks > = comp( cpiece , ptweaks );

export const getTweaks = ( state : State ) : Tweaks => Object.getOwnPropertyNames( state.pieces )
	.map( ( prop : string ) => {
		const piece : undefined | Piece = state.pieces[ prop ];
		return !piece ? { Rulers : {} } : piece.tweaks;
	} )
	.reduce( ( acc : Tweaks , curr : Tweaks ) => mergeFigMod( acc )( curr ) , state.tweaks )
;

export const pan : Optic< State , V2 > = comp( cpiece , ppan );
export const zoom : Optic< State , number > = comp( cpiece , pzoom );
export const ctrd : Optic< State , boolean > = comp( cpiece , pctrd );
export const angle : Optic< State , number > = comp( cpiece , pangle );

export const rulerLayer = ( rid : IId ) : Optic< State , undefined | number > =>
	comp( ctweaks , _RulerById( rid.join('-') ) , withDefault< RulerMod >({}) , _Info() , _Layer );

export const rulerLabelLayer = ( rid : IId ) : Optic< State , undefined | number > =>
	comp( ctweaks , _RulerById( rid.join('-') ) , withDefault< RulerMod >({}) , _Info() , _LabelLayer );

export const mkState = ( figure : Figure ) : State => ( {
	size   : { w : 0 , h : 0 },
	figure : figure,
	tweaks : { Rulers : {} },
	pieces : {},
	active : '0',
	config : defConfig,
});

export const _delPan = mem( ( pan : V2 ) => ( pnt : P2 ) : P2 =>
	Pnt.add( pnt )( Vec.scale( -1 )( pan ) ) );

export const _delZoom = mem( ( zoom : number ) => ( pnt : P2 ) : P2 =>
	Pnt.map( Vec.v2_scale( 1 / zoom ) )( pnt ) );

export const _normalize = mem( ( pan : V2 ) => mem( ( zoom : number ) => ( pnt : P2 ) : P2 =>
	_delPan( pan )( _delZoom( zoom )( pnt ) ) ) );

export const _project = mem( ( a : number ) => ( p : P3 ) : P2 =>
	Pnt.lowerXZ3( Pnt.map( Vec.aboutZ( a ) )( p ) )
);

export interface Page {
	name : string;
	page : string;
}

export const getPages = ( state : State ) : NonEmpty< Page > =>
	state.figure.Pieces.map( ( piece : Fig.Piece , index : number ) =>
		( { name : piece.Title , page : String( index ) } )
	) as NonEmpty< Page >;
